/**
 * Application Settings Admin Page - SystemAdmin only
 * View and manage application-wide settings grouped by category
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  SlidersHorizontal, Loader2, RefreshCw, Plus, Save, X,
  CheckCircle, AlertCircle, Eye, EyeOff, ChevronDown, ChevronRight, Play
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import Navigation from '../../components/Navigation';
import Sidebar from '../../components/Sidebar';
import Footer from '../../components/Footer';
import { getErrorMessage } from '../../services/apiService';
import {
  listApplicationSettings,
  updateApplicationSetting,
  createApplicationSetting,
  ApplicationSettingResponse,
  UpdateApplicationSettingRequest,
  CreateApplicationSettingRequest
} from '../../services/adminSettingsService';
import { makeAuthenticatedRequest, createApiError } from '../../services/apiService';
import { API_ENDPOINTS } from '../../config/api';
import {
  getDepartments, INVOICE_TERMS,
  NetSuiteDepartment,
} from '../../services/netsuiteService';

const ApplicationSettings: React.FC = () => {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();

  const isSystemAdmin = user?.isSystemAdmin === true;

  // Redirect if not SystemAdmin
  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      navigate('/login');
    } else if (!isSystemAdmin) {
      navigate('/dashboard');
    }
  }, [user, authLoading, navigate, isSystemAdmin]);

  const [settings, setSettings] = useState<ApplicationSettingResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Editing state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<UpdateApplicationSettingRequest>({
    value: '',
    longValue: null,
    description: null,
    isPublic: false,
  });
  const [saving, setSaving] = useState(false);

  // Create modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState<CreateApplicationSettingRequest>({
    key: '',
    value: '',
    longValue: null,
    valueType: 'string',
    category: '',
    description: null,
    isPublic: false,
  });
  const [creating, setCreating] = useState(false);

  // Category filter
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());

  // Auto-invoicing invoice defaults config
  const [aiForm, setAiForm] = useState({
    defaultCustomFormId: '', defaultDepartmentId: '', defaultLocationId: '', defaultTermsId: '',
  });
  const [aiSaving, setAiSaving] = useState(false);
  const [aiSuccess, setAiSuccess] = useState('');
  const [aiError, setAiError] = useState('');
  const [aiDepartments, setAiDepartments] = useState<NetSuiteDepartment[]>([]);
  const [aiCatalogLoading, setAiCatalogLoading] = useState(false);

  // Auto-invoicing trigger
  const [autoInvoiceDate, setAutoInvoiceDate] = useState('');
  const [autoInvoiceRunning, setAutoInvoiceRunning] = useState(false);
  const [autoInvoiceResult, setAutoInvoiceResult] = useState<{
    date: string; draftsCreated: number; submittedToNetSuite: number;
    skippedNoActivity: number; skippedAlreadyExists: number; skippedNotDue: number;
    errors: number; messages: string[];
  } | null>(null);
  const [autoInvoiceError, setAutoInvoiceError] = useState('');

  const runAutoInvoicing = async () => {
    setAutoInvoiceRunning(true);
    setAutoInvoiceResult(null);
    setAutoInvoiceError('');
    try {
      const url = API_ENDPOINTS.INVOICES.AUTO_INVOICE_RUN(autoInvoiceDate || undefined);
      const response = await makeAuthenticatedRequest(url, { method: 'POST' });
      if (!response.ok) {
        const err = await createApiError(response);
        throw err;
      }
      const data = await response.json();
      setAutoInvoiceResult(data);
    } catch (err: unknown) {
      setAutoInvoiceError(err instanceof Error ? err.message : 'Run failed');
    } finally {
      setAutoInvoiceRunning(false);
    }
  };

  const saveAiConfig = async () => {
    setAiSaving(true);
    setAiError('');
    setAiSuccess('');
    try {
      const byKey = Object.fromEntries(settings.map(s => [s.key, s]));
      const pairs: [string, string][] = [
        ['AutoInvoicing:DefaultCustomFormId', aiForm.defaultCustomFormId],
        ['AutoInvoicing:DefaultDepartmentId', aiForm.defaultDepartmentId],
        ['AutoInvoicing:DefaultLocationId',   aiForm.defaultLocationId],
        ['AutoInvoicing:DefaultTermsId',      aiForm.defaultTermsId],
      ];
      for (const [key, value] of pairs) {
        const existing = byKey[key];
        if (existing) {
          await updateApplicationSetting(existing.id, {
            value,
            longValue: null,
            description: existing.description,
            isPublic: false,
          });
        } else if (value) {
          await createApplicationSetting({
            key,
            value,
            valueType: 'string',
            category: 'AutoInvoicing',
            description: null,
            isPublic: false,
          });
        }
      }
      setAiSuccess('Auto-invoicing item IDs saved.');
      loadSettings();
      setTimeout(() => setAiSuccess(''), 4000);
    } catch (err) {
      setAiError(getErrorMessage(err));
    } finally {
      setAiSaving(false);
    }
  };

  const loadSettings = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      const data = await listApplicationSettings();
      setSettings(data);
      const byKey = Object.fromEntries(data.map(s => [s.key, s.value]));
      setAiForm({
        defaultCustomFormId: byKey['AutoInvoicing:DefaultCustomFormId'] ?? '',
        defaultDepartmentId: byKey['AutoInvoicing:DefaultDepartmentId'] ?? '',
        defaultLocationId:   byKey['AutoInvoicing:DefaultLocationId']   ?? '',
        defaultTermsId:      byKey['AutoInvoicing:DefaultTermsId']      ?? '',
      });
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && isSystemAdmin) {
      loadSettings();
      setAiCatalogLoading(true);
      getDepartments()
        .then(depts => setAiDepartments(depts))
        .catch(() => {})
        .finally(() => setAiCatalogLoading(false));
    }
  }, [user, isSystemAdmin, loadSettings]);

  // Group settings by category
  const categories = Array.from(new Set(settings.map(s => s.category))).sort();
  const filteredSettings = categoryFilter
    ? settings.filter(s => s.category === categoryFilter)
    : settings;
  const groupedSettings = categories
    .filter(cat => !categoryFilter || cat === categoryFilter)
    .map(cat => ({
      category: cat,
      settings: filteredSettings.filter(s => s.category === cat),
    }))
    .filter(g => g.settings.length > 0);

  const toggleCategory = (cat: string) => {
    setCollapsedCategories(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  const startEditing = (setting: ApplicationSettingResponse) => {
    setEditingId(setting.id);
    setEditForm({
      value: setting.value,
      longValue: setting.longValue,
      description: setting.description,
      isPublic: setting.isPublic,
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
  };

  const handleSave = async (id: string) => {
    setSaving(true);
    setError('');
    try {
      await updateApplicationSetting(id, editForm);
      setEditingId(null);
      setSuccess('Setting updated successfully.');
      loadSettings();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleBooleanToggle = async (setting: ApplicationSettingResponse) => {
    const newValue = setting.value.toLowerCase() === 'true' ? 'False' : 'True';
    setSaving(true);
    setError('');
    try {
      await updateApplicationSetting(setting.id, {
        value: newValue,
        longValue: setting.longValue,
        description: setting.description,
        isPublic: setting.isPublic,
      });
      setSuccess(`${setting.key} set to ${newValue}.`);
      loadSettings();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setError('');
    try {
      await createApplicationSetting(createForm);
      setShowCreateModal(false);
      setCreateForm({ key: '', value: '', longValue: null, valueType: 'string', category: '', description: null, isPublic: false });
      setSuccess('Setting created successfully.');
      loadSettings();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setCreating(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isLongContent = (setting: ApplicationSettingResponse) =>
    setting.valueType === 'html' || setting.valueType === 'markdown';

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#f8fafc] to-[#e2e8f0]">
        <Loader2 className="w-8 h-8 animate-spin text-[#c41e3a]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-[#f8fafc] to-[#e2e8f0] text-[#0f172a]">
      {/* Ambient Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-radial from-[#c41e3a]/[0.03] to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-radial from-[#c41e3a]/[0.04] to-transparent rounded-full blur-3xl"></div>
      </div>

      <Navigation onMenuClick={() => setIsSidebarOpen(true)} />
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <main className="flex-1 lg:ml-[220px] pt-28 md:pt-32 px-4 lg:px-8 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl font-bold text-[#0f172a] flex items-center gap-3">
                <SlidersHorizontal className="w-7 h-7 text-[#c41e3a]" />
                Application Settings
              </h1>
              <p className="text-[#64748b] mt-1">Manage system-wide application configuration</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={loadSettings}
                disabled={isLoading}
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-black/[0.08] bg-white text-[#475569] hover:border-[#c41e3a]/50 transition-all"
              >
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-medium transition-all shadow-lg shadow-green-500/20"
              >
                <Plus className="w-4 h-4" />
                Create New
              </button>
            </div>
          </div>

          {/* Messages */}
          {error && (
            <div className="mb-6 p-4 rounded-xl border flex items-start space-x-3"
              style={{ backgroundColor: 'rgba(220, 38, 38, 0.1)', borderColor: 'rgba(220, 38, 38, 0.3)' }}>
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
              <button onClick={() => setError('')} className="ml-auto">
                <X className="w-4 h-4 text-red-600" />
              </button>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 rounded-xl border flex items-center space-x-3"
              style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)', borderColor: 'rgba(34, 197, 94, 0.3)' }}>
              <CheckCircle className="w-5 h-5 text-green-600" />
              <p className="text-sm text-green-800">{success}</p>
            </div>
          )}

          {/* Auto-Invoicing Trigger */}
          <div className="mb-8 p-6 rounded-xl border border-black/[0.08] bg-white shadow-sm">
            <h2 className="text-base font-semibold text-[#0f172a] flex items-center gap-2 mb-4">
              <Play className="w-4 h-4 text-[#c41e3a]" />
              Auto-Invoicing — Manual Trigger
            </h2>
            <p className="text-sm text-[#64748b] mb-4">
              Runs the auto-invoicing job for a specific date. Only clients with <strong>AutoInvoicingEnabled=true</strong> are
              processed. Use <code className="bg-[#f1f5f9] px-1 rounded text-xs">2026-07-01</code> or{' '}
              <code className="bg-[#f1f5f9] px-1 rounded text-xs">2026-07-15</code> to simulate billing days — leaving the
              date blank uses today (which will skip all clients if today isn't the 1st or 15th).
            </p>
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="block text-xs font-medium text-[#64748b] mb-1">Date (optional)</label>
                <input
                  type="date"
                  value={autoInvoiceDate}
                  onChange={(e) => setAutoInvoiceDate(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-black/[0.08] bg-white text-sm text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/30"
                />
              </div>
              <button
                onClick={runAutoInvoicing}
                disabled={autoInvoiceRunning}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-[#c41e3a] to-[#a01830] hover:from-[#a01830] hover:to-[#8b1528] text-white font-medium transition-all shadow-lg shadow-[#c41e3a]/20 disabled:opacity-60"
              >
                {autoInvoiceRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                {autoInvoiceRunning ? 'Running...' : 'Run Now'}
              </button>
            </div>

            {autoInvoiceError && (
              <div className="mt-4 p-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-800 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                {autoInvoiceError}
              </div>
            )}

            {autoInvoiceResult && (
              <div className="mt-4 space-y-3">
                <div className="flex flex-wrap gap-3">
                  {[
                    { label: 'Drafts Created', value: autoInvoiceResult.draftsCreated, color: 'green' },
                    { label: 'Submitted to NetSuite', value: autoInvoiceResult.submittedToNetSuite, color: 'blue' },
                    { label: 'Skipped (no activity)', value: autoInvoiceResult.skippedNoActivity, color: 'gray' },
                    { label: 'Skipped (exists)', value: autoInvoiceResult.skippedAlreadyExists, color: 'gray' },
                    { label: 'Skipped (not due)', value: autoInvoiceResult.skippedNotDue, color: 'gray' },
                    { label: 'Errors', value: autoInvoiceResult.errors, color: 'red' },
                  ].map(stat => (
                    <div key={stat.label} className="px-3 py-2 rounded-lg border border-black/[0.06] bg-[#f8fafc] text-sm">
                      <span className="text-[#64748b]">{stat.label}: </span>
                      <span className={`font-semibold ${stat.color === 'green' ? 'text-green-700' : stat.color === 'red' ? 'text-red-700' : stat.color === 'blue' ? 'text-blue-700' : 'text-[#475569]'}`}>
                        {stat.value}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="rounded-lg border border-black/[0.06] bg-[#f8fafc] p-3 max-h-48 overflow-y-auto">
                  <p className="text-xs font-medium text-[#475569] mb-2">Run log</p>
                  {autoInvoiceResult.messages.map((msg, i) => (
                    <p key={i} className={`text-xs font-mono ${msg.startsWith('ERROR') ? 'text-red-700' : msg.startsWith('WARN') ? 'text-amber-700' : msg.startsWith('DRAFT') || msg.startsWith('SUBMITTED') ? 'text-green-700' : 'text-[#64748b]'}`}>
                      {msg}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Auto-Invoicing Configuration */}
          <div className="mb-8 p-6 rounded-xl border border-black/[0.08] bg-white shadow-sm">
            <h2 className="text-base font-semibold text-[#0f172a] flex items-center gap-2 mb-1">
              <SlidersHorizontal className="w-4 h-4 text-[#c41e3a]" />
              Auto-Invoicing — Invoice Defaults
            </h2>
            <p className="text-sm text-[#64748b] mb-5">
              Service items (MVR per-state, NMVTIS Record, Theft/Lien Record, VIN Decode) are resolved automatically from
              the NetSuite catalog. Invoice defaults (Form 122, Dept 5, Location 1, Terms 4) match the manual invoicing form —
              only set these if you need to override.
            </p>

            {aiCatalogLoading ? (
              <div className="flex items-center gap-2 text-sm text-[#64748b]">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading NetSuite options…
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs font-medium text-[#64748b] mb-1">Custom Form ID</label>
                  <input
                    type="text"
                    value={aiForm.defaultCustomFormId}
                    onChange={(e) => setAiForm(f => ({ ...f, defaultCustomFormId: e.target.value }))}
                    placeholder="e.g. 122"
                    className="w-full px-3 py-2 rounded-lg border border-black/[0.08] bg-white text-sm text-[#0f172a] placeholder-[#94a3b8] focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/30"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#64748b] mb-1">Department</label>
                  <select
                    value={aiForm.defaultDepartmentId}
                    onChange={(e) => setAiForm(f => ({ ...f, defaultDepartmentId: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-black/[0.08] bg-white text-sm text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/30"
                  >
                    <option value="">— system default —</option>
                    {aiDepartments.map(d => (
                      <option key={d.id} value={d.id ?? ''}>{d.name ?? d.id}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#64748b] mb-1">Location ID</label>
                  <input
                    type="text"
                    value={aiForm.defaultLocationId}
                    onChange={(e) => setAiForm(f => ({ ...f, defaultLocationId: e.target.value }))}
                    placeholder="e.g. 1"
                    className="w-full px-3 py-2 rounded-lg border border-black/[0.08] bg-white text-sm text-[#0f172a] placeholder-[#94a3b8] focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/30"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#64748b] mb-1">Default Terms</label>
                  <select
                    value={aiForm.defaultTermsId}
                    onChange={(e) => setAiForm(f => ({ ...f, defaultTermsId: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-black/[0.08] bg-white text-sm text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/30"
                  >
                    <option value="">— system default (Due on receipt) —</option>
                    {INVOICE_TERMS.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {aiError && (
              <div className="mt-4 p-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-800 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                {aiError}
              </div>
            )}
            {aiSuccess && (
              <div className="mt-4 p-3 rounded-lg border border-green-200 bg-green-50 text-sm text-green-800 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 shrink-0" />
                {aiSuccess}
              </div>
            )}

            <div className="mt-5">
              <button
                onClick={saveAiConfig}
                disabled={aiSaving}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-[#c41e3a] to-[#a01830] hover:from-[#a01830] hover:to-[#8b1528] text-white font-medium transition-all shadow-lg shadow-[#c41e3a]/20 disabled:opacity-60"
              >
                {aiSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {aiSaving ? 'Saving…' : 'Save Item IDs'}
              </button>
            </div>
          </div>

          {/* Category Filter */}
          <div className="mb-6 flex items-center gap-3">
            <label className="text-sm font-medium text-[#64748b]">Filter by category:</label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-3 py-2 rounded-lg border border-black/[0.08] bg-white text-[#0f172a] text-sm focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/30"
            >
              <option value="">All Categories ({settings.length})</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>
                  {cat} ({settings.filter(s => s.category === cat).length})
                </option>
              ))}
            </select>
          </div>

          {/* Settings List */}
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#c41e3a]" />
            </div>
          ) : groupedSettings.length === 0 ? (
            <div className="text-center py-12 text-[#94a3b8]">
              No settings found.
            </div>
          ) : (
            <div className="space-y-6">
              {groupedSettings.map(({ category, settings: catSettings }) => (
                <div key={category} className="glass-card overflow-hidden">
                  {/* Category Header */}
                  <button
                    onClick={() => toggleCategory(category)}
                    className="w-full flex items-center justify-between px-6 py-4 bg-black/[0.02] hover:bg-black/[0.04] transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {collapsedCategories.has(category) ? (
                        <ChevronRight className="w-5 h-5 text-[#64748b]" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-[#64748b]" />
                      )}
                      <h2 className="text-lg font-semibold text-[#0f172a]">{category}</h2>
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#c41e3a]/10 text-[#c41e3a]">
                        {catSettings.length}
                      </span>
                    </div>
                  </button>

                  {/* Settings in this category */}
                  {!collapsedCategories.has(category) && (
                    <div className="divide-y divide-black/[0.06]">
                      {catSettings.map((setting) => {
                        const isEditing = editingId === setting.id;

                        return (
                          <div key={setting.id} className="px-6 py-4 hover:bg-black/[0.01] transition-colors">
                            <div className="flex items-start justify-between gap-4">
                              {/* Left: Key, badges, description */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <code className="text-sm font-semibold text-[#0f172a] font-mono">{setting.key}</code>
                                  <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-500/10 text-blue-700">
                                    {setting.valueType}
                                  </span>
                                  {setting.isPublic ? (
                                    <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-500/10 text-green-700">
                                      <Eye className="w-3 h-3" /> Public
                                    </span>
                                  ) : (
                                    <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-500/10 text-gray-600">
                                      <EyeOff className="w-3 h-3" /> Private
                                    </span>
                                  )}
                                </div>

                                {/* Value display / edit */}
                                {isEditing ? (
                                  <div className="mt-3 space-y-3">
                                    {isLongContent(setting) ? (
                                      <div>
                                        <label className="block text-xs font-medium text-[#64748b] mb-1">Long Value</label>
                                        <textarea
                                          value={editForm.longValue || ''}
                                          onChange={(e) => setEditForm({ ...editForm, longValue: e.target.value || null })}
                                          rows={6}
                                          className="w-full px-3 py-2 rounded-lg border border-black/[0.08] bg-white text-sm font-mono text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/30"
                                        />
                                      </div>
                                    ) : null}
                                    <div>
                                      <label className="block text-xs font-medium text-[#64748b] mb-1">Value</label>
                                      <input
                                        type="text"
                                        value={editForm.value}
                                        onChange={(e) => setEditForm({ ...editForm, value: e.target.value })}
                                        className="w-full px-3 py-2 rounded-lg border border-black/[0.08] bg-white text-sm text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/30"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-[#64748b] mb-1">Description</label>
                                      <input
                                        type="text"
                                        value={editForm.description || ''}
                                        onChange={(e) => setEditForm({ ...editForm, description: e.target.value || null })}
                                        className="w-full px-3 py-2 rounded-lg border border-black/[0.08] bg-white text-sm text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/30"
                                      />
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <label className="text-xs font-medium text-[#64748b]">Public:</label>
                                      <button
                                        type="button"
                                        onClick={() => setEditForm({ ...editForm, isPublic: !editForm.isPublic })}
                                        className={`relative w-10 h-5 rounded-full transition-colors ${editForm.isPublic ? 'bg-[#c41e3a]' : 'bg-gray-300'}`}
                                      >
                                        <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${editForm.isPublic ? 'translate-x-5' : ''}`} />
                                      </button>
                                    </div>
                                    <div className="flex items-center gap-2 pt-1">
                                      <button
                                        onClick={() => handleSave(setting.id)}
                                        disabled={saving}
                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#c41e3a] to-[#e74860] text-white text-sm font-medium hover:from-[#a51830] hover:to-[#c41e3a] transition-all disabled:opacity-50"
                                      >
                                        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                                        Save
                                      </button>
                                      <button
                                        onClick={cancelEditing}
                                        className="px-3 py-1.5 rounded-lg border border-black/[0.08] text-[#64748b] text-sm hover:bg-black/[0.03] transition-colors"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="mt-1">
                                    {setting.valueType === 'boolean' ? (
                                      <button
                                        onClick={() => handleBooleanToggle(setting)}
                                        disabled={saving}
                                        className={`relative w-10 h-5 rounded-full transition-colors disabled:opacity-50 ${
                                          setting.value.toLowerCase() === 'true' ? 'bg-green-500' : 'bg-gray-300'
                                        }`}
                                      >
                                        <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                                          setting.value.toLowerCase() === 'true' ? 'translate-x-5' : ''
                                        }`} />
                                      </button>
                                    ) : (
                                      <p className="text-sm text-[#475569] font-mono truncate max-w-lg" title={setting.value}>
                                        {setting.value || <span className="italic text-[#94a3b8]">empty</span>}
                                      </p>
                                    )}
                                    {setting.description && (
                                      <p className="text-xs text-[#94a3b8] mt-1">{setting.description}</p>
                                    )}
                                  </div>
                                )}
                              </div>

                              {/* Right: Actions and metadata */}
                              <div className="flex flex-col items-end gap-2 flex-shrink-0">
                                {!isEditing && (
                                  <button
                                    onClick={() => startEditing(setting)}
                                    className="px-3 py-1.5 rounded-lg border border-black/[0.08] text-[#475569] text-sm hover:border-[#c41e3a]/50 hover:text-[#c41e3a] transition-colors"
                                  >
                                    Edit
                                  </button>
                                )}
                                <span className="text-[10px] text-[#94a3b8]">
                                  Updated {formatDate(setting.updatedAt)}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />

      {/* Create Setting Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl max-w-lg w-full p-6 border border-black/[0.08] shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-[#0f172a]">Create New Setting</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-2 hover:bg-black/[0.05] rounded-lg transition-colors text-[#64748b]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-[#0f172a]">Key</label>
                <input
                  type="text"
                  value={createForm.key}
                  onChange={(e) => setCreateForm({ ...createForm, key: e.target.value })}
                  placeholder="e.g., Feature.NewDashboard"
                  required
                  className="w-full px-4 py-2 rounded-xl border border-black/[0.08] bg-white/80 text-[#0f172a] placeholder-[#94a3b8] focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/30"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1 text-[#0f172a]">Category</label>
                  <input
                    type="text"
                    value={createForm.category}
                    onChange={(e) => setCreateForm({ ...createForm, category: e.target.value })}
                    placeholder="e.g., Feature"
                    required
                    list="existing-categories"
                    className="w-full px-4 py-2 rounded-xl border border-black/[0.08] bg-white/80 text-[#0f172a] placeholder-[#94a3b8] focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/30"
                  />
                  <datalist id="existing-categories">
                    {categories.map(cat => <option key={cat} value={cat} />)}
                  </datalist>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-[#0f172a]">Value Type</label>
                  <select
                    value={createForm.valueType}
                    onChange={(e) => setCreateForm({ ...createForm, valueType: e.target.value })}
                    className="w-full px-4 py-2 rounded-xl border border-black/[0.08] bg-white/80 text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/30"
                  >
                    <option value="string">string</option>
                    <option value="boolean">boolean</option>
                    <option value="integer">integer</option>
                    <option value="decimal">decimal</option>
                    <option value="html">html</option>
                    <option value="markdown">markdown</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-[#0f172a]">Value</label>
                <input
                  type="text"
                  value={createForm.value}
                  onChange={(e) => setCreateForm({ ...createForm, value: e.target.value })}
                  required
                  className="w-full px-4 py-2 rounded-xl border border-black/[0.08] bg-white/80 text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/30"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-[#0f172a]">Description</label>
                <input
                  type="text"
                  value={createForm.description || ''}
                  onChange={(e) => setCreateForm({ ...createForm, description: e.target.value || null })}
                  placeholder="What does this setting control?"
                  className="w-full px-4 py-2 rounded-xl border border-black/[0.08] bg-white/80 text-[#0f172a] placeholder-[#94a3b8] focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/30"
                />
              </div>

              <div className="flex items-center justify-between p-3 rounded-xl border border-black/[0.08]">
                <span className="text-sm font-medium text-[#0f172a]">Publicly accessible</span>
                <button
                  type="button"
                  onClick={() => setCreateForm({ ...createForm, isPublic: !createForm.isPublic })}
                  className={`relative w-11 h-6 rounded-full transition-colors ${createForm.isPublic ? 'bg-[#c41e3a]' : 'bg-gray-200'}`}
                >
                  <span className={`absolute top-[2px] left-[2px] w-5 h-5 rounded-full bg-white border border-gray-300 transition-transform ${createForm.isPublic ? 'translate-x-5' : ''}`} />
                </button>
              </div>

              <div className="flex space-x-3 pt-2">
                <button
                  type="submit"
                  disabled={creating}
                  className="flex-1 px-4 py-2 rounded-xl bg-gradient-to-r from-green-600 to-green-700 text-white font-medium disabled:opacity-50 hover:from-green-700 hover:to-green-800 transition-all flex items-center justify-center gap-2"
                >
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  {creating ? 'Creating...' : 'Create Setting'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationSettings;
